'use client'

import { ReactNode, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGames } from '../hooks/useGames'
import LuckyIcon from '../components/lucky/LuckyIcon'
import { GuessGlyph, MorseGlyph, TrophyGlyph } from '../components/lucky/ProfileGlyphs'
import { useT } from '../contexts/LocaleContext'

type GameCard = {
  key: string
  titleKey: string
  subtitleKey: string
  blurbKey?: string
  path: string
  ctaKey: string
  icon: ReactNode
  accent: string
}

export default function GamesPage() {
  const navigate = useNavigate()
  const t = useT()
  const { status, loading, error } = useGames()

  const games: GameCard[] = useMemo(
    () => [
      {
        key: 'guess',
        titleKey: 'games.cards.guess.title',
        subtitleKey: 'games.cards.guess.subtitle',
        path: '/games/guess-number/play',
        ctaKey: 'common.play',
        icon: <GuessGlyph size={42} />,
        accent: 'guess',
      },
      {
        key: 'lucky',
        titleKey: 'games.cards.lucky.title',
        subtitleKey: 'games.cards.lucky.subtitle',
        blurbKey: 'games.cards.lucky.blurb',
        path: '/games/lucky-numbers/play',
        ctaKey: 'common.play',
        icon: <LuckyIcon name="logo" size={36} alt="" className="lucky-games__logo" />,
        accent: 'lucky',
      },
      {
        key: 'morse',
        titleKey: 'games.cards.morse.title',
        subtitleKey: 'games.cards.morse.subtitle',
        blurbKey: 'games.cards.morse.blurb',
        path: '/games/morse/play',
        ctaKey: 'common.play',
        icon: <MorseGlyph size={42} />,
        accent: 'morse',
      },
      {
        key: 'horse',
        titleKey: 'games.cards.horse.title',
        subtitleKey: 'games.cards.horse.subtitle',
        blurbKey: 'games.cards.horse.blurb',
        path: '/horse',
        ctaKey: 'common.go',
        icon: <TrophyGlyph size={40} />,
        accent: 'horse',
      },
    ],
    [],
  )

  return (
    <div className="lucky-games mini-app-container pb-20 min-h-screen">
      <div className="lucky-games__inner">
        <header className="lucky-games__header">
          <div className="lucky-games__header-icon">
            <LuckyIcon name="game" size={40} alt="" />
          </div>
          <div>
            <h1>{t('games.title')}</h1>
            <p>{t('games.subtitle')}</p>
          </div>
        </header>

        {loading ? (
          <div className="lucky-games__state">{t('common.loading')}</div>
        ) : error ? (
          <div className="lucky-games__state lucky-games__state--err">{error}</div>
        ) : (
          <div className="lucky-games__list">
            {games.map((g) => (
              <article key={g.key} className={`lucky-games__card lucky-games__card--${g.accent}`}>
                <div className="lucky-games__card-top">
                  <div className="lucky-games__thumb">{g.icon}</div>
                  <div className="lucky-games__card-text">
                    <h2>{t(g.titleKey)}</h2>
                    <p>{t(g.subtitleKey)}</p>
                  </div>
                </div>

                {g.key === 'guess' && status && (
                  <ul className="lucky-games__meta">
                    <li>
                      <span>{t('games.status')}</span>
                      <strong>
                        {status.is_revealed
                          ? t('games.revealed')
                          : status.has_active_number
                            ? t('games.active')
                            : t('games.waiting')}
                      </strong>
                    </li>
                    <li>
                      <span>{t('games.chances')}</span>
                      <strong>{t('games.of10', { n: status.attempts_remaining })}</strong>
                    </li>
                    {status.is_revealed && status.current_number && (
                      <li>
                        <span>{t('games.todayNumber')}</span>
                        <code>{status.current_number}</code>
                      </li>
                    )}
                  </ul>
                )}

                {g.blurbKey && <p className="lucky-games__blurb">{t(g.blurbKey)}</p>}

                <button
                  type="button"
                  className="lucky-games__cta"
                  onClick={() => navigate(g.path)}
                >
                  {t(g.ctaKey)}
                </button>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
